import React, { useState, useEffect } from 'react';
import { a2aDeveloperService } from '../../a2a/services/a2aDeveloperService.js';
import './APIKeysModule.css';

const APIKeysModule = ({ currentUser, onBack, onTabChange }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    setLoading(true);
    try {
      const result = await a2aDeveloperService.getAPIKeys({
        developer_id: currentUser?.developer_id || currentUser?.id,
        tenant_id: currentUser?.tenant_id || 'tn_01'
      });
      if (result.success) {
        setApiKeys(result.data || []);
      } else {
        setMessage('獲取 API 金鑰失敗: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      setMessage('錯誤: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!keyName.trim()) {
      setMessage('請輸入金鑰名稱');
      return;
    }

    setLoading(true);
    try {
      const result = await a2aDeveloperService.generateAPIKey({
        developer_id: currentUser?.developer_id || currentUser?.id,
        name: keyName,
        tenant_id: currentUser?.tenant_id || 'tn_01'
      });
      if (result.success) {
        setMessage('API 金鑰生成成功！請妥善保存。');
        setKeyName('');
        setShowCreateForm(false);
        fetchAPIKeys();
      } else {
        setMessage('生成失敗: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      setMessage('錯誤: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!window.confirm('確定要重新生成此 API 金鑰嗎？舊金鑰將立即失效。')) {
      return;
    }

    setLoading(true);
    try {
      // First revoke, then generate new
      await a2aDeveloperService.revokeAPIKey({
        developer_id: currentUser?.developer_id || currentUser?.id,
        key_id: keyId,
        tenant_id: currentUser?.tenant_id || 'tn_01'
      });
      
      const result = await a2aDeveloperService.generateAPIKey({
        developer_id: currentUser?.developer_id || currentUser?.id,
        name: apiKeys.find(k => k.id === keyId)?.name || 'Regenerated Key',
        tenant_id: currentUser?.tenant_id || 'tn_01'
      });
      
      if (result.success) {
        setMessage('API 金鑰已重新生成！');
        fetchAPIKeys();
      } else {
        setMessage('重新生成失敗: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      setMessage('錯誤: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('確定要撤銷此 API 金鑰嗎？此操作無法撤銷。')) {
      return;
    }

    setLoading(true);
    try {
      const result = await a2aDeveloperService.revokeAPIKey({
        developer_id: currentUser?.developer_id || currentUser?.id,
        key_id: keyId,
        tenant_id: currentUser?.tenant_id || 'tn_01'
      });
      
      if (result.success) {
        setMessage('API 金鑰已撤銷');
        fetchAPIKeys();
      } else {
        setMessage('撤銷失敗: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      setMessage('錯誤: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const APIKeyCard = ({ apiKey, onRegenerate, onRevoke }) => {
    const [showKey, setShowKey] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      if (apiKey.full_key) {
        navigator.clipboard.writeText(apiKey.full_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    };

    return (
      <div className="api-key-card">
        <div className="api-key-header">
          <h3>{apiKey.name}</h3>
          <span className={`status-badge ${apiKey.status}`}>{apiKey.status === 'active' ? '啟用' : '已撤銷'}</span>
        </div>
        <div className="api-key-info">
          <div className="api-key-value">
            <span className="key-prefix">{apiKey.key_prefix}</span>
            {apiKey.full_key && (
              <>
                {showKey ? (
                  <span className="key-suffix">{apiKey.full_key.replace(apiKey.key_prefix, '')}</span>
                ) : (
                  <span className="key-masked">••••••••••••••••</span>
                )}
                <button 
                  className="toggle-key-btn"
                  onClick={() => setShowKey(!showKey)}
                >
                  {showKey ? '隱藏' : '顯示'}
                </button>
                <button 
                  className="copy-key-btn"
                  onClick={handleCopy}
                >
                  {copied ? '已複製' : '複製'}
                </button>
              </>
            )}
          </div>
          <div className="api-key-meta">
            <span>創建時間: {new Date(apiKey.created_at).toLocaleString('zh-TW')}</span>
            {apiKey.last_used && (
              <span>最後使用: {new Date(apiKey.last_used).toLocaleString('zh-TW')}</span>
            )}
          </div>
        </div>
        <div className="api-key-actions">
          <button onClick={() => onRegenerate(apiKey.id)} disabled={loading}>
            重新生成
          </button>
          <button onClick={() => onRevoke(apiKey.id)} disabled={loading} className="danger">
            撤銷
          </button>
        </div>
      </div>
    );
  };

  const CreateAPIKeyForm = ({ onSubmit, onCancel }) => {
    return (
      <div className="create-key-form">
        <h3>生成新 API 金鑰</h3>
        <div className="form-group">
          <label>金鑰名稱:</label>
          <input
            type="text"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="例如: Production Key, Test Key"
          />
        </div>
        <div className="form-actions">
          <button onClick={onSubmit} disabled={loading || !keyName.trim()}>
            {loading ? '生成中...' : '生成金鑰'}
          </button>
          <button onClick={onCancel} disabled={loading}>
            取消
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="api-keys-module">
      <div className="module-header">
        <button onClick={onBack} className="back-btn">← 返回</button>
        <h2>API 金鑰管理</h2>
        <button 
          onClick={() => setShowCreateForm(true)} 
          className="create-btn"
          disabled={loading}
        >
          ➕ 生成新金鑰
        </button>
      </div>

      {message && (
        <div className={`message ${message.includes('成功') || message.includes('已') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}
      
      {showCreateForm && (
        <CreateAPIKeyForm
          onSubmit={handleGenerateKey}
          onCancel={() => {
            setShowCreateForm(false);
            setKeyName('');
            setMessage('');
          }}
        />
      )}
      
      {loading && !showCreateForm && (
        <div className="loading">載入中...</div>
      )}

      <div className="api-keys-list">
        {apiKeys.length === 0 && !loading ? (
          <div className="empty-state">
            <p>尚未創建任何 API 金鑰</p>
            <button onClick={() => setShowCreateForm(true)}>創建第一個金鑰</button>
          </div>
        ) : (
          apiKeys.map(key => (
            <APIKeyCard
              key={key.id}
              apiKey={key}
              onRegenerate={handleRegenerateKey}
              onRevoke={handleRevokeKey}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default APIKeysModule;

